import type { DateTime, Interval } from 'luxon';
import { createSelector } from 'reselect';

import { constructPredefinedInterval, type PREDEFINED_RANGE_VALUE } from './predefinedRange';

export type DateRangeOption = {
  label: string;
  value: Interval;
  id: string;
  indent?: boolean; // render this option indented in the shortcuts list
  dividerAfter?: boolean; // render a divider after this option in the shortcuts list
};

export type DateRangeDef = {
  label: string;
  id: PREDEFINED_RANGE_VALUE;
  indent?: boolean;
  dividerAfter?: boolean;
};

export const DATE_RANGE_DEFS: Array<DateRangeDef> = [
  { label: 'Month to date', id: 'MONTH_TO_DATE' },
  { label: 'Quarter to date', id: 'QUARTER_TO_DATE' },
  { label: 'Year to date', id: 'YEAR_TO_DATE', dividerAfter: true },
  { label: 'Last month', id: 'LAST_MONTH' },
  { label: 'Last quarter', id: 'LAST_QUARTER' },
  { label: 'Last year', id: 'LAST_YEAR', dividerAfter: true },
  { label: 'Recent 2 months', id: 'RECENT_2_MONTHS' },
  { label: 'Recent 3 months', id: 'RECENT_3_MONTHS' },
  { label: 'Recent 6 months', id: 'RECENT_6_MONTHS' },
  { label: 'Recent 12 months', id: 'RECENT_12_MONTHS', dividerAfter: true },
];

export const DATE_RANGE_LABEL_BY_ID: Partial<Record<PREDEFINED_RANGE_VALUE, string>> = Object.fromEntries(
  DATE_RANGE_DEFS.map(({ id, label }) => [id, label]),
);

const computeDateRange = (def: DateRangeDef, today: DateTime): DateRangeOption => ({
  label: def.label,
  value: constructPredefinedInterval({ value: def.id }, today),
  id: def.id,
  indent: def.indent,
  dividerAfter: def.dividerAfter,
});

const computeDateRangeOptions = (today: DateTime): DateRangeOption[] =>
  DATE_RANGE_DEFS.map((def) => computeDateRange(def, today));

export const buildDateRangeOptions = createSelector([(today: DateTime) => today], computeDateRangeOptions);
